import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getAttendanceStatusLabel } from '../../utils/attendanceLabels';
import {
  ATTENDANCE_CHIP_COLORS,
  COVERAGE_CHIP_COLORS,
} from '../../constants/operationsDashboardColors';
import {
  formatCoverageModeLabel,
  normalizeTimeForInput,
  canShowAddCoverageMenuItem,
  canShowAssignCoverageMenuItem,
} from '../../utils/coverageDashboardUtils';

function formatCoverageWindow(row: any): string {
  const start = normalizeTimeForInput(row.coverageStartTime);
  const end = normalizeTimeForInput(row.coverageEndTime);
  if (!start && !end) return '-';
  if (start && end) return `${start} - ${end}`;
  return start || end || '-';
}

function formatCoverageTypeLabel(type?: string | null): string {
  if (!type) return '-';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

interface EmployeeDetailDrawerProps {
  open: boolean;
  row: any | null;
  onClose: () => void;
  onViewTimeline: (row: any) => void;
  onAssignCoverage: (row: any) => void;
  onRemoveCoverage?: (row: any) => void;
  canManage: boolean;
}

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

const EmployeeDetailDrawer: React.FC<EmployeeDetailDrawerProps> = ({
  open,
  row,
  onClose,
  onViewTimeline,
  onAssignCoverage,
  onRemoveCoverage,
  canManage,
}) => {
  if (!row) return null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
      <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>{row.agentName}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto' }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Employee Information</Typography>
            <DetailRow label="Campaign" value={row.campaignName} />
            <DetailRow label="Employee ID" value={row.employeeIdCode || row.employeeId} />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Schedule</Typography>
            <DetailRow label="Shift" value={`${row.scheduledStartTime} - ${row.scheduledEndTime}`} />
            <DetailRow label="Original Campaign" value={row.originalCampaignName || row.campaignName} />
            <DetailRow label="Current Assignment" value={row.currentAssignmentCampaignName || row.campaignName} />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Attendance</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={getAttendanceStatusLabel(row.attendanceStatus)}
                sx={{ bgcolor: ATTENDANCE_CHIP_COLORS[row.attendanceStatus], color: '#fff' }}
              />
              <Chip
                size="small"
                label={row.coverageStatus.replace(/_/g, ' ')}
                sx={{ bgcolor: COVERAGE_CHIP_COLORS[row.coverageStatus], color: '#fff' }}
              />
            </Stack>
            <DetailRow label="Clock In" value={row.clockInTime || '-'} />
            <DetailRow label="Clock Out" value={row.clockOutTime || '-'} />
            <DetailRow label="Late Minutes" value={row.lateMinutes > 0 ? row.lateMinutes : '-'} />
            <DetailRow label="Details" value={row.attendanceDetails || '-'} />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Coverage</Typography>
            {['COVERED', 'PARTIALLY_COVERED'].includes(row.coverageStatus) && (
              <>
                <DetailRow label="Coverage Time" value={formatCoverageWindow(row)} />
                <DetailRow label="Coverage Duration" value={row.coverageDurationLabel || '-'} />
                <DetailRow label="Coverage Type" value={formatCoverageTypeLabel(row.coverageType)} />
              </>
            )}
            <DetailRow label="Absence Reason" value={row.absenceReasonLabel || '-'} />
            <DetailRow label="Coverage Reason" value={row.coverageReasonLabel || '-'} />
            <DetailRow label="Coverage Mode" value={formatCoverageModeLabel(row.coverageMode)} />
            <DetailRow label="Covered By" value={row.coveredByName || '-'} />
            <DetailRow
              label="Customer Notified"
              value={row.customerNotified ? 'Yes — customer informed' : 'No — not yet notified'}
            />
            <DetailRow label="Covering For" value={row.coveringFor?.join(', ') || '-'} />
            <DetailRow label="Notes" value={row.notes || '-'} />
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ pt: 2, flexWrap: 'wrap' }}>
          <Button fullWidth variant="outlined" onClick={() => onViewTimeline(row)} sx={{ flex: '1 1 45%' }}>
            Campaign Timeline
          </Button>
          {canManage && canShowAssignCoverageMenuItem(row) && (
            <Button fullWidth variant="contained" onClick={() => onAssignCoverage(row)} sx={{ flex: '1 1 45%' }}>
              {row.coverageAssignmentId ? 'Change Coverage' : 'Assign Coverage'}
            </Button>
          )}
          {canManage && canShowAddCoverageMenuItem(row) && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => onAssignCoverage({ ...row, coverageAssignmentId: null, coveredById: null, coveredByName: null })}
              sx={{ flex: '1 1 45%' }}
            >
              Add Coverage
            </Button>
          )}
          {canManage && row.coverageAssignmentId && onRemoveCoverage && (
            <Button fullWidth variant="outlined" color="error" onClick={() => onRemoveCoverage(row)} sx={{ flex: '1 1 100%' }}>
              Remove Coverage
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default EmployeeDetailDrawer;
